import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import axios from "axios";
import { SEO, buildProductJsonLd } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { HeroImage } from "@/components/HeroImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BuyNowButton } from "@/components/BuyNowButton";
import { AddToCartButton } from "@/components/AddToCartButton";
import { ProductOptions } from "@/components/ProductOptions";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n?.toLocaleString("en-NZ", { minimumFractionDigits: n % 1 !== 0 ? 2 : 0 });

const CATEGORY_LABEL = { loadbar: "Load Bar", mount: "Awning & Tent Mount", accessory: "Accessory", platform: "Platform Rack" };

export default function RoofRackDetailPage() {
  const { slug } = useParams();
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/roofrack`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  const product = products?.find((p) => p.slug === slug);
  const hasOptions = product?.optionGroups?.length > 0;
  const [optionsState, setOptionsState] = useState({ selectedOptions: {}, totalPrice: product?.price || 0, allRequiredSelected: !hasOptions, combinationInStock: true });
  const recentlyViewed = useRecentlyViewed(
    product && { path: `/roof-racks/${product.slug}`, name: product.name, thumb: product.thumb, price: product.price, priceFrom: product.priceFrom }
  );

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!product) return <Navigate to="/roof-racks" replace />;

  const related = products.filter((p) => p.type === product.type && p.slug !== product.slug).slice(0, 3);
  const gallery = product.gallery && product.gallery.length > 0 ? product.gallery.map(normalizeGalleryImage) : [{ src: product.thumb, alt: null, type: "image", pos: "center" }];
  const canBuyNow = product.price != null && !product.priceOnRequest && product.inStock !== false;

  return (
    <div data-testid="roofrack-detail-page">
      <SEO
        path={`/roof-racks/${product.slug}`}
        title={`${product.name} · EEZI-AWN K9 · XpeditioNZ`}
        description={`${product.name} — ${product.tagline}. ${product.intro.slice(0, 140)}`}
        image={product.hero || product.thumb}
        jsonLd={buildProductJsonLd({ name: product.name, description: product.tagline, image: product.hero || product.thumb, price: product.price, priceOnRequest: product.priceOnRequest, inStock: product.inStock, url: `/roof-racks/${product.slug}` })}
      />

      <section className="relative min-h-[50svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage src={product.hero || product.thumb} alt={product.name} className="w-full h-full" desktopPosition={product.heroPosition || "50% center"} mobilePosition={product.heroFocalMobile} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-14 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/roof-racks" data-testid="rr-detail-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)]">
              ← Roof Racks
            </Link>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
              {CATEGORY_LABEL[product.type]}
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[0.95] text-white hero-text-shadow max-w-3xl">
            {product.name.toUpperCase()}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-4">
            {product.variants} — {product.tagline}
          </p>
          <div className="mt-7 flex items-start gap-6 flex-wrap">
            <div>
              <p className="font-display text-3xl md:text-4xl text-white leading-none">
                {product.price != null ? (
                  <>
                    {(product.priceFrom && !hasOptions) && <span className="text-base font-mono uppercase tracking-widest text-white/70 mr-2">FROM</span>}
                    ${fmt(hasOptions ? optionsState.totalPrice : product.price)}
                  </>
                ) : (
                  product.availability
                )}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                {product.price != null && `GST included · `}{product.availability}
              </p>
            </div>
            <div className="flex items-start gap-3 flex-wrap">
              {canBuyNow && (
                <>
                  <BuyNowButton
                    productPath={`/roof-racks/${product.slug}`}
                    testId="rr-detail-cta-buy-now"
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                    disabledHint={hasOptions && optionsState.allRequiredSelected && !optionsState.combinationInStock ? "" : "Select all options below to continue"}
                  />
                  <AddToCartButton
                    productPath={`/roof-racks/${product.slug}`}
                    testId="rr-detail-cta-add-to-cart"
                    name={product.name}
                    thumb={product.thumb}
                    unitPrice={hasOptions ? optionsState.totalPrice : product.price}
                    priceFrom={product.priceFrom && !hasOptions}
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                  />
                </>
              )}
              <a href="#enquire" data-testid="rr-detail-cta-enquire" className="btn-secondary">Enquire About This <ArrowUpRight size={14} /></a>
            </div>
          </div>
          {hasOptions && (
            <div className="mt-8 max-w-lg">
              <ProductOptions optionGroups={product.optionGroups} variantStock={product.variantStock || []} basePrice={product.price} onChange={setOptionsState} />
            </div>
          )}
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <ProductGallery gallery={gallery} productName={product.name} />
          </div>
          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="overline mb-4">— About This Product</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">{product.intro}</p>
            </div>
            {product.features && product.features.length > 0 && (
              <div className="border-t border-[var(--border-default)] pt-8">
                <p className="overline mb-5">— Key Features</p>
                <ul className="space-y-3">
                  {product.features.map((f, i) => (
                    <li key={f || i} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                      <Check size={16} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {product.contentSections && product.contentSections.length > 0 && (
        <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <div className="container-x py-16 md:py-20">
            <div className="mb-12 max-w-2xl">
              <p className="overline mb-4">— More About This Product</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                BUILT TO A <span className="text-[var(--brand-primary)]">DIFFERENT STANDARD.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12">
              {product.contentSections.map((section, i) => (
                <div key={section.heading || i} data-testid={`content-section-${i}`} className="border-t border-[var(--border-default)] pt-6">
                  {section.heading && (
                    <h3 className="font-display text-xl md:text-2xl text-white mb-3">{section.heading}</h3>
                  )}
                  {section.body && (
                    <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed whitespace-pre-line">{section.body}</p>
                  )}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {section.bullets.map((b, bi) => (
                        <li key={b || bi} className="flex items-start gap-2 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                          <Check size={14} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.linkText && section.linkHref && (
                    <Link
                      to={section.linkHref}
                      data-testid={`content-section-link-${i}`}
                      className="inline-flex items-center gap-1.5 mt-3 text-sm font-mono uppercase tracking-wider text-[var(--brand-primary)] hover:underline"
                    >
                      {section.linkText} <ArrowUpRight size={13} />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="container-x pb-14 md:pb-20 border-t border-[var(--border-default)] pt-14 md:pt-20">
          <div className="mb-8">
            <p className="overline mb-3">— More {CATEGORY_LABEL[product.type]}s</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              YOU MIGHT ALSO <span className="text-[var(--brand-primary)]">NEED.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link key={r.slug} to={`/roof-racks/${r.slug}`} data-testid={`related-rr-${i}`} className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <HoverImage primary={r.thumb} secondary={r.gallery?.[1]?.src} alt={r.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-lg md:text-xl text-white leading-tight">{r.name}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {r.price != null ? `${r.priceFrom ? "FROM " : ""}$${fmt(r.price)}` : r.availability}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RecentlyViewedStrip items={recentlyViewed} />

      <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Enquire or Order</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                ASK US<br /><span className="text-[var(--brand-primary)]">ANYTHING.</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-6 leading-relaxed">
                Fitment questions, stock levels, freight to your region — get proper answers.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                <EnquiryForm defaultProduct={`Roof Rack — ${product.name}`} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
